import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/auth";
import { eur } from "@/lib/constants";

const PREPARATION_SLUGS = [
  "fichier-conforme",
  "planche-pdf-prete",
  "adapter-format",
  "recomposition-graphique",
  "controle-prepresse",
  "adapter-couverture",
  "restauration-image",
];

const ROUTER_CHOICES = [
  { label: "Le mettre au bon format", to: "/configurer/format" },
  { label: "Rendre mon fichier conforme à l'imprimeur", to: "/configurer/conforme" },
  { label: "Créer une planche PDF prête à imprimer", to: "/configurer/planche" },
  { label: "Vérifier mon fichier avant impression", to: "/commander/controle-prepresse" },
  { label: "Je ne sais pas", to: "/aide-choix" },
];

export default function PreparerMonProjet() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)); }, []);
  const list = PREPARATION_SLUGS
    .map((slug) => services.find((s) => s.slug === slug))
    .filter(Boolean);

  return (
    <div data-testid="page-preparer-mon-projet">
      {/* Hero */}
      <section className="cs-cream border-b border-black/10 py-20 lg:py-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Parcours · Préparation de fichier</div>
          <h1 className="cs-h1 mt-4 text-5xl lg:text-7xl">J'ai déjà<br /><span className="italic font-light text-[#0E7C90]">mon projet.</span></h1>
          <p className="mt-8 font-serif text-2xl lg:text-3xl max-w-3xl leading-snug">Votre création existe déjà. Charbonier Studio s'occupe de la rendre exploitable.</p>
          <p className="mt-6 text-neutral-700 max-w-2xl text-[15px] sm:text-base leading-relaxed">
            Transmettez votre fichier et indiquez ce que vous souhaitez obtenir. Il peut être adapté, contrôlé,
            corrigé ou préparé pour l'impression. Charbonier Studio ne réinvente pas votre design — il le rend imprimable.
          </p>
        </div>
      </section>

      {/* Routeur — Que voulez-vous faire ? */}
      <section className="cs-cream py-16 border-b border-black/10" data-testid="preparation-router">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Que souhaitez-vous faire avec votre fichier ?</div>
          <h2 className="cs-h2 mt-3 text-3xl lg:text-4xl">Trouvez la prestation en un clic.</h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ROUTER_CHOICES.map((c, i) => (
              <Link key={i} to={c.to}
                className="group flex items-center justify-between border border-black/15 bg-white hover:bg-black hover:text-[#FAF8F5] transition-colors px-5 py-5"
                data-testid={`router-choice-${i}`}>
                <span className="font-serif text-lg leading-snug">{c.label}</span>
                <ArrowUpRight size={18} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Toutes les prestations préparation */}
      <section className="cs-cream py-16 border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Prestations disponibles</div>
          <h2 className="cs-h2 mt-3 text-3xl lg:text-4xl">Préparer, corriger, adapter, contrôler.</h2>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {list.map((s) => (
              <Link key={s.id} to={`/prestation/${s.slug}`} className="cs-card p-8 flex flex-col justify-between min-h-[280px]" data-testid={`prep-${s.slug}`}>
                <div>
                  <h3 className="cs-h3 text-2xl">{s.name}</h3>
                  <p className="mt-3 text-[15px] text-neutral-600 line-clamp-3">{s.short_description}</p>
                  {s.tiers?.length > 0 && (
                    <ul className="mt-3 space-y-1 text-[13px]">
                      {s.tiers.slice(0, 3).map((t, i) => (
                        <li key={i} className={`flex justify-between ${t.highlight ? "text-black font-medium" : "text-neutral-500"}`}>
                          <span>{t.label}</span>
                          <span className="font-mono">{t.note || `${t.price} €`}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="mt-6 pt-4 border-t border-black/10 flex items-end justify-between">
                  <div>
                    <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-neutral-500">À partir de</div>
                    <div className="font-serif text-3xl">{eur(s.starting_price)}</div>
                  </div>
                  <ArrowUpRight size={20} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Précision importante */}
      <section className="cs-dark cs-grain py-20 border-b border-white/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-400">Important</div>
          <h3 className="cs-h2 mt-3 text-3xl lg:text-4xl">Ces tarifs ne comprennent pas la création originale d'un visuel.</h3>
          <p className="mt-6 text-[15px] text-neutral-300 leading-relaxed max-w-2xl">
            Les prestations « J'ai déjà mon projet » interviennent sur un fichier ou un visuel <span className="text-[#FAF8F5]">déjà finalisé graphiquement</span>.
            Si Charbonier Studio doit également concevoir votre design, choisissez plutôt le parcours « Je veux créer un support ».
          </p>
          <p className="mt-4 text-[15px] text-neutral-300 leading-relaxed max-w-2xl">
            <span className="text-[#FAF8F5]">Aucun supplément sans votre accord.</span> Si votre demande dépasse le périmètre commandé, vous êtes prévenu avant toute intervention.
          </p>
          <div className="mt-8">
            <Link to="/creer-mon-support" className="cs-btn-ghost">Vous voulez plutôt créer un support ? →</Link>
          </div>
        </div>
      </section>

      {/* SOS Print rappel */}
      <section className="cs-cream py-20 text-center">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="cs-eyebrow text-[#0E7C90]">SOS Print</div>
          <h3 className="cs-h2 mt-3 text-3xl lg:text-4xl">Votre imprimeur refuse votre fichier ?</h3>
          <p className="mt-4 text-neutral-700 max-w-xl mx-auto text-[15px]">Cas urgent : deux tarifs clairs selon la gravité du problème.</p>
          <div className="mt-6 flex justify-center flex-wrap gap-4">
            <Link to="/sos-print" className="cs-btn-primary">Voir SOS Print <ArrowUpRight size={18} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
